import { LeaderTabKernelHost, } from "./leader-election.js";
import { createRpcTransport } from "./kernel-connector.js";
import { KERNEL_WAKE_MESSAGE, SharedWorkerKernelHost } from "./shared-worker-host.js";
export function createBrowserKernelHost(options) {
    if (!options.forceLeaderTab && typeof globalThis.SharedWorker === "function") {
        const transports = new Set();
        const host = new SharedWorkerKernelHost({
            createWorker: (name) => {
                const worker = new globalThis.SharedWorker(options.sharedWorkerUrl, { name, type: "module" });
                return { port: nativeMessagePort(worker.port) };
            },
            name: options.workerName,
        });
        return {
            close: async () => {
                for (const transport of [...transports])
                    transport.close();
                transports.clear();
            },
            connect: () => {
                const transport = host.connect();
                transports.add(transport);
                transport.subscribeClose(() => transports.delete(transport));
                return transport;
            },
            wake: async () => { host.wake(); },
            mode: "shared-worker",
        };
    }
    const locks = globalThis.navigator?.locks;
    if (locks === undefined)
        throw new Error("This browser does not support SharedWorker or Web Locks");
    const localStorage = browserLocalStorage();
    const host = new LeaderTabKernelHost({
        channelName: options.channelName,
        createChannel: (name) => new globalThis.BroadcastChannel(name),
        ...(localStorage === undefined ? {} : { localStorage }),
        locks: locks,
        runtimeFactory: options.runtimeFactory,
        tabId: globalThis.crypto.randomUUID(),
    });
    return {
        close: async () => await host.close(),
        connect: () => host.connect(),
        wake: async () => await host.wake(),
        mode: "leader-tab",
    };
}
export function hostBrowserSharedWorker(scope, runtime) {
    const workerScope = scope;
    workerScope.onconnect = (event) => {
        const port = event.ports[0];
        if (port === undefined)
            return;
        port.addEventListener("message", (message) => {
            if (message.data === KERNEL_WAKE_MESSAGE)
                void Promise.resolve(runtime.wake?.()).catch(() => undefined);
        });
        const transport = createRpcTransport(nativeMessagePort(port));
        const detach = runtime.attach(transport);
        transport.subscribeClose(detach);
        port.start();
    };
}
const PORT_CLOSED_MESSAGE = "\u0000node-client:rpc-port-closed";
class NativeMessageEndpoint {
    #closeListeners = new Set();
    #messageListeners = new Set();
    #port;
    #closed = false;
    constructor(port) {
        this.#port = port;
        this.#port.addEventListener("message", (event) => {
            if (event.data === PORT_CLOSED_MESSAGE) {
                this.#handleClose();
            }
            else if (event.data === KERNEL_WAKE_MESSAGE) {
                // The worker host handles wake requests outside the RPC protocol.
            }
            else if (typeof event.data === "string" && !this.#closed) {
                for (const listener of [...this.#messageListeners])
                    listener(event.data);
            }
        });
    }
    close() {
        if (this.#closed)
            return;
        this.#port.postMessage(PORT_CLOSED_MESSAGE);
        this.#port.close();
        this.#handleClose();
    }
    postMessage(message) {
        if (!this.#closed)
            this.#port.postMessage(message);
    }
    start() {
        this.#port.start();
    }
    subscribe(listener) {
        this.#messageListeners.add(listener);
        return () => this.#messageListeners.delete(listener);
    }
    subscribeClose(listener) {
        this.#closeListeners.add(listener);
        return () => this.#closeListeners.delete(listener);
    }
    #handleClose() {
        if (this.#closed)
            return;
        this.#closed = true;
        for (const listener of [...this.#closeListeners])
            listener();
        this.#closeListeners.clear();
        this.#messageListeners.clear();
    }
}
function nativeMessagePort(port) {
    return new NativeMessageEndpoint(port);
}
function browserLocalStorage() {
    try {
        return globalThis.localStorage;
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=browser-kernel-host.js.map